import {
	Checkbox,
	Form,
	FormItem,
	TextInput,
	ValidationError
} from "client/Components";
import {
	AddTilesResponseModel,
	YouTubeUpdateTileModel,
	YouTubeDetailsTileModel,
	YouTubeCreateTileModel,
	createYouTubeTile,
	updateYouTubeTile
} from "client/Api/Workspace/workspace";
import { EditTileFromData, initialGenericState } from "client/Features/Workspace/LeftSliders/EditTile/EditGenericTile";
import { EditTileHandle } from "client/Features/Workspace/LeftSliders/EditTile/EditTile";
import { EditTileGeneralFields } from "client/Features/Workspace/LeftSliders/EditTile/GeneralFields";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	TileEditMode,
} from "./common";
import { useCreateTile } from "./hooks/useCreateTile";
import { useUpdateTile } from "./hooks/useUpdateTile";

type YouTubeEditFormData = EditTileFromData<YouTubeUpdateTileModel>;

const validationBuilder: IValidationConfiguration<YouTubeEditFormData> = createValidationConfiguration<YouTubeEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationBuilder.forScalar("youTubeUrl")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type EditYouTubeTileProps = {
	initialModel: YouTubeDetailsTileModel;
	mode: TileEditMode;
	isLoading: boolean;
	onIsLoadingChange: (value: boolean) => void;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditYouTubeTile = forwardRef(
	(
		props: EditYouTubeTileProps,
		ref: Ref<EditTileHandle>
	) => {
		const {
			initialModel,
			mode,
			onIsLoadingChange,
			isLoading,
			onAfterSave,
		} = props;

		const [formData, setFormData] = useState<YouTubeEditFormData>({
			...initialGenericState(initialModel),
			youTubeUrl: initialModel.youTubeUrl,
			hideHeader: initialModel.hideHeader,
			hideFooter: initialModel.hideFooter
		});

		const formHandler: IFormHandler<YouTubeEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { createHandler } = useCreateTile<YouTubeCreateTileModel>(
			formHandler,
			createYouTubeTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		const { updateHandler } = useUpdateTile<YouTubeUpdateTileModel>(
			formHandler,
			updateYouTubeTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		useImperativeHandle(
			ref,
			() => ({ createHandler, updateHandler })
		);

		return (
			<Form
				onSubmit="NONE"
			>
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<FormItem
					label={localize("_VideoOrPlaylistUrl")}
				>
					<TextInput
						{...formHandler.for("youTubeUrl").attachToTextInput()}
						placeholder={localize("_VideoOrPlaylistUrlPlaceholder")}
					/>
					<ValidationError
						{
							...formHandler
								.for("youTubeUrl")
								.attachToValidationError()
						}
					/>
				</FormItem>
				<FormItem>
					<Checkbox
						{...formHandler.for("hideHeader").attachToCheckbox()}
						text={localize("_HideTileHeader")}
					/>
				</FormItem>
				<FormItem>
					<Checkbox
						{...formHandler.for("hideFooter").attachToCheckbox()}
						text={localize("_HideTileFooter")}
					/>
				</FormItem>
			</Form>
		);
	}
);
EditYouTubeTile.displayName = "EditYouTubeTile";
